import { useCallback, useEffect, useState } from 'react';
import { isRoomCode, normalizeRoomCode } from '@planning-session/shared';

export type Route =
  | { readonly name: 'landing' }
  | { readonly name: 'room'; readonly roomId: string }
  | { readonly name: 'retro'; readonly roomId: string };

export function parseRoute(pathname: string): Route {
  const roomMatch = /^\/room\/([^/]+)\/?$/.exec(pathname);
  if (roomMatch) {
    const roomId = normalizeRoomCode(roomMatch[1] ?? '');
    if (isRoomCode(roomId)) return { name: 'room', roomId };
  }

  const retroMatch = /^\/retro\/([^/]+)\/?$/.exec(pathname);
  if (retroMatch) {
    const roomId = normalizeRoomCode(retroMatch[1] ?? '');
    if (isRoomCode(roomId)) return { name: 'retro', roomId };
  }

  return { name: 'landing' };
}

export function roomPath(roomId: string): string {
  return `/room/${roomId}`;
}

export function retroPath(roomId: string): string {
  return `/retro/${roomId}`;
}

/**
 * Two routes is not worth a routing library. This keeps room URLs real and
 * shareable (the server falls back to index.html) with a popstate listener.
 */
export function useRoute(): { route: Route; navigate: (path: string) => void } {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.pathname));

  useEffect(() => {
    const onPopState = () => setRoute(parseRoute(window.location.pathname));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const navigate = useCallback((path: string) => {
    if (path === window.location.pathname) return;
    window.history.pushState({}, '', path);
    setRoute(parseRoute(path));
  }, []);

  return { route, navigate };
}
